import * as React from 'react';
import { compose } from 'redux';

import type { AddItemToCartParams } from '@shopee/add-to-cart-redux';
import type { ItemDetail, State as RootState } from '@shopee/flow-types';
import type { OnTrackType } from '@shopee/tracking-hoc';
import type { RouteComponentProps } from 'react-router';
import type { ComponentWithPopupTypes } from '@shopee/rw-with-popup';

import { connect } from 'react-redux';
import { withRouter } from 'react-router';

import { withPopup } from '@shopee/rw-with-popup';
import { trackingAction, withOnTrackAsProps } from '@shopee/tracking-hoc';
import { withAccountInfo } from '@shopee/account-hoc';
import { AccountStatus } from '@shopee/account-utils';
import type { WithAccountInfoProps } from '@shopee/account-utils';

import {
  addItemToCartWithToastMessages,
  requestShowAddToCartPanel,
  CartClientId,
} from '@shopee/add-to-cart-redux';
import {
  fetchItemDetailCached as fetchItemDetail,
  passItemDataFromList,
} from '@shopee/item-redux';
import {
  loginByPassword,
  appendRedirectUrl,
} from '@shopee_common/authentication-url';
import { setOfficialShopTheme } from './actions';

import Toast from '@stardust/toast';
import LazyLoad from '@stardust/lazy-load';
import Portal from '@stardust/portal';
import Spinner from '@stardust/spinner';
import IconToastExclamation from '@nebula/icon/icons/IconToastExclamation36';

import { productPage, getProductPath } from './utils/url';
import { t, ADULT_AGE, LOCALE } from './config';
import { getUserAdultV2 } from '@shopee/is-adult';

import {
  AddToCartContext,
  shouldRedirectToProductPage,
} from '@shopee/add-item-to-cart';
import type { RequestAddItemToCart } from '@shopee/add-item-to-cart';
export { withAddToCartAsProp } from '@shopee/add-item-to-cart';
export type { WithAddToCartAsPropProps } from '@shopee/add-item-to-cart';
export { AddToCartContext };

import styles from './hocs.style.scss';

const AddToCartDrawer = LazyLoad({
  loader: () =>
    import(
      /* webpackChunkName: "AddToCartDrawer" */
      './Drawer'
    ),
  skipLazyTrigger: true,
});

type State = {
  isLoading: boolean;
  itemId: number | null | undefined;
  shopId: number | null | undefined;
  isAddToCartDrawerActive: boolean;
  selectedModelId: number | null;
  quantity: number;
  toastMessage: string | null | undefined;
  toastErrorCode: number | null | undefined;
};

type PropsFromStore = {
  isUserAdult: boolean;
  isLoggedIn: boolean;
  loggedInUserShopId: number;
  requestShowAddToCartPanel: (
    shopId: number,
    itemId: number,
    {
      forceShowCartPanel,
      getItemById,
    }?: {
      forceShowCartPanel?: boolean;
      getItemById?: (state: RootState, itemId: number) => ItemDetail | null;
    }
  ) => Promise<{
    shouldShowAddToCartPanel: boolean;
  }>;
  fetchItemDetail: (
    shopId: number,
    itemId: number,
    getItemById?:
      | ((state: RootState, itemId: number) => ItemDetail | null)
      | undefined
  ) => Promise<ItemDetail>;
  addItemToCartWithToastMessages: (item: AddItemToCartParams) => Promise<
    | {
        toastMessage: any;
        errorCode: number;
        cartItem?: undefined;
        usePopup?: boolean;
      }
    | {
        cartItem: any;
        toastMessage: string;
        errorCode: number;
        usePopup?: boolean;
      }
  >;
  setOfficialShopTheme: typeof setOfficialShopTheme;
  passItemDataFromList: typeof passItemDataFromList;
};

type PropsFromOtherHocs = RouteComponentProps &
  ComponentWithPopupTypes & {
    contextOnTrack: OnTrackType;
  } & WithAccountInfoProps;

type PropsPassedDown = {
  itemId?: number;
  shopId?: number;
  onAddToCartSuccess?: () => void;
  anchor?: HTMLElement | null | undefined;
  shouldStopPropagation?: boolean;
};

type ComponentWithAddToCartProps = PropsPassedDown &
  PropsFromStore &
  PropsFromOtherHocs;

/**
 * HOC Goal: Expose at React Context API a function with signature
 * (shopId: number, itemId: number, onAddToCartSuccess?: () => void) => void
 * This HOC handles
 * - Whether need to fetch Item Detail from API
 * - Showing AddToCartDrawer if necessary
 * - Calling Add To Cart API
 * - Showing toast messages
 * - Showing loading screen
 * - Blocking add to cart and redirect to PDP if item/account is special
 *
 * This HOC looks duplicated with `pc/app/components/AddToCart/hoc.js`,
 * which is true, but there are subtle differences in implementation due to platform differences,
 * hence it's very hard to abstract those logic out.
 * It's simpler to just duplicate them
 */
export type WithAddToCartConfig = {
  disablePurchaseWithLimit?: boolean;
  customAddToCartMessage?: string;
  hideStrikethroughPrice?: boolean;
};

export function withAddToCart<Config extends WithAddToCartConfig>(
  WrappedComponent: React.ComponentType<Config>,
  options?: { cartClientId: CartClientId }
): React.ComponentType<Config> {
  class ComponentWithAddToCart extends React.Component<
    Config & ComponentWithAddToCartProps,
    State
  > {
    modelQuantities: WeakMap<{ itemId: number; modelId: number }, number>;
    onAddToCartSuccess: ((itemParam: AddItemToCartParams) => void) | null;
    constructor(props: Config & ComponentWithAddToCartProps) {
      super(props);
      this.state = {
        itemId: null,
        shopId: null,
        isAddToCartDrawerActive: false,
        selectedModelId: null,
        quantity: 1,
        toastMessage: null,
        toastErrorCode: null,
        isLoading: false,
      };
      this.modelQuantities = new WeakMap();
      this.onAddToCartSuccess = null;
    }

    requestAddItemToCart: RequestAddItemToCart = async (
      shopId,
      itemId,
      onAddToCartSuccess,
      extraAddToCartTrackingData,
      _,
      spuInfo
      // eslint-disable-next-line
    ) => {
      const isLoggedIn = await this.navigateToLoginPageIfNotLoggedIn();
      if (!isLoggedIn) {
        return;
      }

      this.setState({
        isLoading: true,
        itemId,
        shopId,
        selectedModelId: null,
        quantity: 1,
      });

      this.onAddToCartSuccess = onAddToCartSuccess ? onAddToCartSuccess : null;
      const isSpu = !!spuInfo;
      const { shouldShowAddToCartPanel } = isSpu
        ? { shouldShowAddToCartPanel: false }
        : await this.props.requestShowAddToCartPanel(shopId, itemId);
      const item = isSpu
        ? null
        : await this.props.fetchItemDetail(shopId, itemId, undefined);

      this.setState({ isLoading: false });
      const shouldRedirectToPdp =
        (isSpu && this.props.loggedInUserShopId === shopId) ||
        (!isSpu &&
          (!item ||
            shouldRedirectToProductPage(item, {
              isUserAdult: this.props.isUserAdult,
              loggedInUserShopId: this.props.loggedInUserShopId,
            })));
      if (shouldRedirectToPdp) {
        this.navigateToProductPage(item, { shopId, itemId, ...spuInfo });
        return;
      }

      if (!shouldShowAddToCartPanel) {
        const models = item ? item.models : null;
        this.addItemToCart({
          itemId: isSpu ? itemId : item!.itemid,
          shopId: isSpu ? shopId : item!.shopid,
          virtualSkuInfo: spuInfo,
          quantity: 1,
          modelId: isSpu
            ? spuInfo.rModelId
            : models && models.length
            ? models[0].modelid
            : null,
          ...extraAddToCartTrackingData,
        });
        return;
      }

      this.setState({
        isAddToCartDrawerActive: true,
      });
    };
    hideAddToCartDrawer = () => {
      this.setState({
        isAddToCartDrawerActive: false,
      });
    };

    navigateToProductPage = (
      item: ItemDetail | null | undefined,
      params: {
        itemId: number;
        shopId: number;
        vItemId?: number;
        vShopId?: number;
        vModelId?: number;
        rModelId?: number;
      }
    ) => {
      const { history, passItemDataFromList, setOfficialShopTheme } =
        this.props;
      if (item) {
        passItemDataFromList(item);
        setOfficialShopTheme(item.is_official_shop);
        history.push(
          productPage({
            seoName: item.name,
            shopId: item.shopid,
            itemId: item.itemid,
          })
        );
      } else {
        history.push(getProductPath(params));
      }
    };

    navigateToLoginPageIfNotLoggedIn = () => {
      return new Promise(resolve => {
        if (!this.props.isLoggedIn) {
          this.props.history.push(
            appendRedirectUrl(loginByPassword.getUrl(), { next: location.href })
          );
          resolve(false);
        }

        resolve(true);
      });
    };

    /**
     * @param {AddItemToCartParams} item Item to add to cart
     * @return {Promise<boolean>} promise containing true if addToCart Success, false otherwise.
     */
    addItemToCart = async (item: AddItemToCartParams): Promise<boolean> => {
      const {
        customAddToCartMessage,
        addItemToCartWithToastMessages,
        showPopup,
      } = this.props;
      const { errorCode, toastMessage, usePopup } =
        await addItemToCartWithToastMessages({
          ...item,
          cart_client_id: options?.cartClientId ?? CartClientId.ITEM_CARD,
        });

      if (!errorCode && !!this.onAddToCartSuccess) {
        this.onAddToCartSuccess(item);
      }
      this.hideAddToCartDrawer();
      if (customAddToCartMessage && !errorCode) {
        this.showToast(customAddToCartMessage as string);
      } else if (usePopup) {
        showPopup({
          message: t(toastMessage),
          mainButtonLabel: t('label_ok'),
        });
      } else if (toastMessage) {
        this.setState({ toastErrorCode: errorCode });
        this.showToast(t(toastMessage));
      }

      if (!errorCode) {
        await trackingAction({
          action: 'addToCart',
          data: item,
          track: this.props.contextOnTrack,
        });
        return true;
      }
      return false;
    };

    selectModel = (modelId: number | null | undefined) => {
      const { itemId } = this.state;
      if (!itemId) {
        return;
      }
      if (typeof modelId !== 'number') {
        this.setState({
          selectedModelId: null,
        });
      } else {
        const key = {
          itemId,
          modelId: modelId,
        };
        const modelQuantity = this.modelQuantities.has(key)
          ? this.modelQuantities.get(key) || 1
          : 1;
        this.modelQuantities.set(key, modelQuantity);

        this.setState({
          selectedModelId: modelId,
          quantity: modelQuantity,
        });
      }
    };
    setQuantity = (_quantity: string | number) => {
      const quantity =
        typeof _quantity === 'string' ? parseInt(_quantity, 10) : _quantity;
      this.setState({
        quantity,
      });
      const { itemId, selectedModelId } = this.state;
      if (itemId && selectedModelId) {
        const key = {
          itemId: itemId,
          modelId: selectedModelId,
        };
        this.modelQuantities.set(key, quantity);
      }
    };

    showToast = (message: string) => {
      this.setState({
        toastMessage: message,
      });
    };
    hideToast = () => {
      this.setState({
        toastMessage: null,
        toastErrorCode: null,
      });
    };

    render() {
      const {
        isLoading,
        isAddToCartDrawerActive,
        shopId,
        itemId,
        toastMessage,
        toastErrorCode,
      } = this.state;

      // Take out props so it won't clutter the wrapped component's props
      const {
        isLoggedIn,
        loggedInUserShopId,
        requestShowAddToCartPanel,
        fetchItemDetail,
        addItemToCartWithToastMessages,
        setOfficialShopTheme,
        passItemDataFromList,
        contextOnTrack,
        disablePurchaseWithLimit,
        popupElement,
        anchor,
        hideStrikethroughPrice,
        shouldStopPropagation = true,
        ...rest
      } = this.props;

      return (
        <div
          onClick={e => {
            shouldStopPropagation && e.stopPropagation();
          }}
        >
          {isLoading ? (
            <Portal anchor={anchor}>
              <div className={styles.addToCartSpinner}>
                <Spinner show={isLoading} />
              </div>
            </Portal>
          ) : null}
          {itemId && shopId && (
            <AddToCartDrawer
              isActive={isAddToCartDrawerActive}
              onClose={this.hideAddToCartDrawer}
              shopId={shopId}
              itemId={itemId}
              addItemToCart={this.addItemToCart}
              selectModel={this.selectModel}
              setQuantity={this.setQuantity}
              quantity={this.state.quantity}
              selectedModelId={this.state.selectedModelId}
              addToCartButton={true}
              buyNowButton={false}
              disabled={false}
              rememberLastSelection={false}
              showImagePerVariation={true}
              isLoggedIn={isLoggedIn}
              disablePurchaseWithLimit={disablePurchaseWithLimit}
              autoSelectSingleModel={true}
              anchor={anchor}
              hideStrikethroughPrice={hideStrikethroughPrice}
            />
          )}
          <AddToCartContext.Provider value={this.requestAddItemToCart}>
            <WrappedComponent {...(rest as any)} />
          </AddToCartContext.Provider>
          {toastMessage !== null && (
            <Toast
              {...(toastErrorCode
                ? { toastIcon: <IconToastExclamation /> }
                : {})}
              onToastDismiss={this.hideToast}
              showToast={true}
              toastText={toastMessage}
              anchorClassName="stardust-toast__anchor"
            />
          )}
          {popupElement}
        </div>
      );
    }
  }

  return compose<React.ComponentType<Config>>(
    withPopup,
    withRouter,
    withOnTrackAsProps(),
    withAccountInfo(),
    connect(
      (_: RootState, ownProps: ComponentWithAddToCartProps) => {
        const { accountInfo, accountStatus } = ownProps;
        let isUserAdult = false;
        if (typeof ownProps.isUserAdult === 'boolean') {
          isUserAdult = ownProps.isUserAdult;
        } else {
          isUserAdult = getUserAdultV2(
            accountInfo,
            ADULT_AGE,
            LOCALE
          ).isUserAdult;
        }
        return {
          isLoggedIn: accountStatus === AccountStatus.LOGGED_IN,
          loggedInUserShopId:
            accountInfo && accountInfo.shopId ? accountInfo.shopId : null,
          isUserAdult,
        };
      },
      {
        requestShowAddToCartPanel,
        fetchItemDetail,
        addItemToCartWithToastMessages,
        passItemDataFromList,
        setOfficialShopTheme,
      }
    )
  )(ComponentWithAddToCart);
}
